// --- React ---
import React, { useEffect, useRef, useMemo } from 'react';

// --- 외부 라이브러리 ---
import { GoogleMap, useLoadScript, Marker, Polyline } from '@react-google-maps/api';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// ==================== 타입 정의 ====================

interface MonitoringDataPoint {
  created_at: string;
  latitude?: number | null;
  longitude?: number | null;
  [key: string]: any;
}

interface PlaybackMapProps {
  /** 전체 모니터링 데이터 목록 (시간순 정렬) */
  monitoringData: MonitoringDataPoint[];
  /** 현재 선택된 모니터링 데이터 */
  selectedMonitoring: MonitoringDataPoint | null;
  /** 디바이스 정보 */
  deviceInfo: {
    name?: string;
    serial_number: string;
    device_type: 'belllite' | 'bellmini';
    sensor_types?: string[];
    unit?: string | string[];
    is_connected?: boolean;
  };
}

// ==================== 상수 ====================

const defaultCenter = {
  lat: 37.5665,
  lng: 126.978,
};

const MAX_MARKERS = 500;

const containerStyle = {
  width: '100%',
  height: '100%',
};

// ==================== 유틸 함수 ====================

function getDataPointStatus(
  dataPoint: MonitoringDataPoint,
  deviceType: 'belllite' | 'bellmini'
): 'error' | 'warning' | 'normal' {
  if (deviceType === 'belllite') {
    const errorCodes = dataPoint.error_code || [];
    const warningCodes = dataPoint.warning_code || [];
    const hasError = Array.isArray(errorCodes) && errorCodes.some((code: number) => code !== 0);
    if (hasError) return 'error';
    const hasWarning =
      Array.isArray(warningCodes) && warningCodes.some((code: number) => code !== 0);
    if (hasWarning) return 'warning';
  } else {
    const errorCode = dataPoint.error_code;
    const warningCode = dataPoint.warning_code;
    if (errorCode !== undefined && errorCode !== null && errorCode !== 0) return 'error';
    if (warningCode !== undefined && warningCode !== null && warningCode !== 0) return 'warning';
  }
  return 'normal';
}

function decimateArray<T>(data: T[], maxCount: number): T[] {
  if (data.length <= maxCount) return data;
  const step = Math.ceil(data.length / maxCount);
  const result: T[] = [];
  for (let i = 0; i < data.length; i += step) {
    result.push(data[i]);
  }
  if (result[result.length - 1] !== data[data.length - 1]) {
    result.push(data[data.length - 1]);
  }
  return result;
}

// ==================== 컴포넌트 ====================

/**
 * 히스토리 모니터링 경로 표시 맵
 * - 모니터링 데이터의 위치를 점선으로 연결
 * - 각 데이터 포인트를 작은 원으로 표시 (최대 500개로 데시메이션)
 * - 선택된 데이터 포인트를 활성화된 마커로 강조
 */
const PlaybackMapComponent: React.FC<PlaybackMapProps> = ({
  monitoringData,
  selectedMonitoring,
  deviceInfo,
}) => {
  const { theme, themeMode } = useTheme();
  const mapRef = useRef<google.maps.Map | null>(null);
  const prevCoordRef = useRef<{ lat: number; lng: number } | null>(null);
  const dataIdRef = useRef<MonitoringDataPoint[] | null>(null);

  // ==================== 다크모드 스타일 ====================

  const darkMapStyles: google.maps.MapTypeStyle[] = useMemo(
    () => [
      { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
      {
        featureType: 'administrative.locality',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
      },
      {
        featureType: 'poi',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#263c3f' }],
      },
      {
        featureType: 'poi.park',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#6b9a76' }],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#38414e' }],
      },
      {
        featureType: 'road',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#212a37' }],
      },
      {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#9ca5b3' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#746855' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#1f2835' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#f3d19c' }],
      },
      {
        featureType: 'transit',
        elementType: 'geometry',
        stylers: [{ color: '#2f3948' }],
      },
      {
        featureType: 'transit.station',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#d59563' }],
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#17263c' }],
      },
      {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#515c6d' }],
      },
      {
        featureType: 'water',
        elementType: 'labels.text.stroke',
        stylers: [{ color: '#17263c' }],
      },
    ],
    []
  );

  // Google Maps API Key
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
  });

  // ==================== 메모이제이션된 파생 데이터 ====================

  const validLocations = useMemo(
    () =>
      monitoringData.filter(
        d =>
          d.latitude !== null &&
          d.latitude !== undefined &&
          d.longitude !== null &&
          d.longitude !== undefined
      ),
    [monitoringData]
  );

  const pathCoordinates = useMemo(
    () => validLocations.map(d => ({ lat: d.latitude!, lng: d.longitude! })),
    [validLocations]
  );

  // 마커용 데시메이션 (최대 MAX_MARKERS개)
  const decimatedLocations = useMemo(
    () => decimateArray(validLocations, MAX_MARKERS),
    [validLocations]
  );

  // 선택된 데이터의 좌표 (lat/lng 값이 같으면 같은 참조 유지)
  const selectedCoordinate = useMemo(() => {
    if (!selectedMonitoring?.latitude || !selectedMonitoring?.longitude) return null;
    return { lat: selectedMonitoring.latitude, lng: selectedMonitoring.longitude };
  }, [selectedMonitoring?.latitude, selectedMonitoring?.longitude]);

  // 상태별 아이콘 캐싱 (3가지 상태만 존재)
  const cachedIcons = useMemo(() => {
    if (typeof google === 'undefined') return null;

    const colors = {
      error: theme.colors.status.danger.stroke.icon,
      warning: theme.colors.status.caution.stroke.icon,
      normal: theme.colors.status.stable.stroke.icon,
    };

    const pointIcons = Object.fromEntries(
      (['error', 'warning', 'normal'] as const).map(status => [
        status,
        {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 3,
          fillColor: colors[status],
          fillOpacity: 0.8,
          strokeColor: '#ffffff',
          strokeWeight: 1,
        },
      ])
    ) as Record<'error' | 'warning' | 'normal', google.maps.Symbol>;

    const buildPersonIcon = (fillColor: string) => {
      const personSvg = `
        <svg width="32" height="32" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
          <path d="M719.57,272.035c18.461,0,35.451,3.565,50.975,10.698 c46.078,23.193,73.285,67.272,73.631,116.424V737.73c-8.643,53.824-70.25,39.188-71.742,0V418.037 c-5.49-18.231-40.693-10.672-41.535,0v733.785c-8.951,74.928-105.773,52.934-108.242-1.258c0-149.778,0-299.558,0-449.334 c-6.191-28.982-46.936-15.813-47.829,1.259c1.728,149.353,1.259,298.715,1.259,448.075c-9.656,74.543-106.007,55.47-108.243,1.258 l-1.258-733.785c-5.643-17.838-38.263-10.996-39.019,0V737.73c-8.644,53.824-70.25,39.188-71.742,0V399.157 c0.871-47.056,18.117-94.197,59.156-116.424c13.426-7.133,29.788-10.699,49.087-10.699 C549.235,272.035,634.402,272.035,719.57,272.035L719.57,272.035z M718.301,118.277c0,65.323-52.955,118.278-118.278,118.278 c-65.322,0-118.277-52.955-118.277-118.278C481.745,52.955,534.7,0,600.021,0C665.346,0,718.301,52.955,718.301,118.277z" 
                fill="${fillColor}" 
                stroke="#ffffff" 
                stroke-width="40"/>
        </svg>
      `;
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(personSvg)}`,
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 32),
      };
    };

    const activeIcons = Object.fromEntries(
      (['error', 'warning', 'normal'] as const).map(status => [
        status,
        buildPersonIcon(colors[status]),
      ])
    ) as Record<'error' | 'warning' | 'normal', google.maps.Icon>;

    return { pointIcons, activeIcons };
  }, [theme, isLoaded]);

  // 경로 선 스타일 (점선)
  const pathOptions = useMemo(
    () => ({
      strokeColor: 'transparent',
      strokeOpacity: 0,
      strokeWeight: 0,
      geodesic: true,
      icons: [
        {
          icon: {
            path: 'M 0,-2 0,2',
            strokeOpacity: 1,
            strokeColor: themeMode === 'dark' ? '#6b7280' : '#9ca3af',
            strokeWeight: 2,
            scale: 2,
          },
          offset: '0',
          repeat: '12px',
        },
      ],
    }),
    [themeMode]
  );

  // ==================== 이펙트 ====================

  /** 데이터가 변경될 때만 fitBounds 실행 (playback tick에서는 실행하지 않음) */
  useEffect(() => {
    if (monitoringData === dataIdRef.current) return;
    dataIdRef.current = monitoringData;

    if (mapRef.current && pathCoordinates.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      pathCoordinates.forEach(coord => bounds.extend(coord));
      mapRef.current.fitBounds(bounds);
    }
  }, [monitoringData, pathCoordinates]);

  /** 선택된 좌표 변경 시 부드럽게 이동 */
  useEffect(() => {
    if (!mapRef.current || !selectedCoordinate) return;
    prevCoordRef.current = selectedCoordinate;
    mapRef.current.panTo(selectedCoordinate);
  }, [selectedCoordinate]);

  // ==================== 렌더링 ====================

  if (loadError) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors.surface.layout.overlay,
          borderRadius: theme.borderRadius.md,
          color: theme.colors.status.danger.stroke.border,
        }}
      >
        Google Maps 로드 실패: {loadError.message}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors.surface.layout.overlay,
          borderRadius: theme.borderRadius.md,
        }}
      >
        지도 로딩 중...
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors.surface.layout.overlay,
          borderRadius: theme.borderRadius.md,
        }}
      >
        Google Maps API Key is required.
      </div>
    );
  }

  if (validLocations.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors.surface.layout.overlay,
          borderRadius: theme.borderRadius.md,
          color: theme.colors.text.secondary,
        }}
      >
        No monitoring data with location information.
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <GoogleMap
        key={deviceInfo.serial_number}
        mapContainerStyle={containerStyle}
        center={pathCoordinates[0] || defaultCenter}
        zoom={15}
        onLoad={map => {
          mapRef.current = map;
          if (pathCoordinates.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            pathCoordinates.forEach(coord => bounds.extend(coord));
            map.fitBounds(bounds);
          }
        }}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          styles: themeMode === 'dark' ? darkMapStyles : undefined,
        }}
      >
        {/* 경로 선 (점선) — 전체 경로 유지 */}
        {pathCoordinates.length > 1 && <Polyline path={pathCoordinates} options={pathOptions} />}

        {/* 데시메이션된 데이터 포인트 (작은 원, 최대 500개) */}
        {cachedIcons &&
          decimatedLocations.map((dataPoint, index) => {
            const isSelected =
              selectedMonitoring && dataPoint.created_at === selectedMonitoring.created_at;
            if (isSelected) return null;

            const status = getDataPointStatus(dataPoint, deviceInfo.device_type);
            return (
              <Marker
                key={`point-${index}`}
                position={{ lat: dataPoint.latitude!, lng: dataPoint.longitude! }}
                icon={cachedIcons.pointIcons[status]}
              />
            );
          })}

        {/* 선택된 데이터 포인트 (활성화된 마커) */}
        {selectedCoordinate && selectedMonitoring && cachedIcons && (
          <Marker
            key={`active-${selectedMonitoring.created_at}`}
            position={selectedCoordinate}
            icon={
              cachedIcons.activeIcons[
                getDataPointStatus(selectedMonitoring as MonitoringDataPoint, deviceInfo.device_type)
              ]
            }
            zIndex={1000}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export const PlaybackMap = React.memo(PlaybackMapComponent);
